import { isValid, parseISO, startOfDay, subMonths } from 'date-fns'

import { CLIENT_CARE_TEAM_INACTIVE_MONTHS } from '@/config/constants'

import type { AppointmentRecord } from '@/features/appointments/types'
import { parseAppointmentDate } from '@/features/appointments/utils/parseAppointmentDate'
import type { VisitRecord } from '@/features/visits/types'

/**
 * Statuses that take an appointment off the calendar. A cancelled visit
 * tomorrow is not the client's next appointment.
 */
const INACTIVE_APPOINTMENT_STATUSES = new Set([
  'cancelled',
  'canceled',
  'no-show',
  'no show',
  'noshow',
  'declined',
  'rescheduled',
])

/**
 * When an appointment happens. `start_time` is the real instant when the feed
 * has one; otherwise fall back to the source's calendar date.
 */
export function appointmentDate(appointment: AppointmentRecord): Date | null {
  if (appointment.start_time) {
    const start = parseISO(appointment.start_time)
    if (isValid(start)) return start
  }
  return parseAppointmentDate(appointment.source_appointment_date)
}

export function isAppointmentActive(appointment: AppointmentRecord): boolean {
  const status = (appointment.status ?? appointment.source_status ?? '')
    .trim()
    .toLowerCase()
  return !INACTIVE_APPOINTMENT_STATUSES.has(status)
}

/**
 * Appointments split around today: upcoming soonest first, past newest first.
 * Undated rows go at the end of past, so every appointment is listed once.
 */
export function splitAppointments(
  appointments: AppointmentRecord[],
  now: Date
): { upcoming: AppointmentRecord[]; past: AppointmentRecord[] } {
  const today = startOfDay(now).getTime()
  const entries = appointments.map((appointment) => ({
    appointment,
    date: appointmentDate(appointment),
  }))
  const dated = entries.filter(
    (entry): entry is { appointment: AppointmentRecord; date: Date } =>
      entry.date !== null
  )
  const undated = entries
    .filter((entry) => entry.date === null)
    .map((entry) => entry.appointment)

  const upcoming = dated
    .filter((entry) => entry.date.getTime() >= today)
    .toSorted((a, b) => a.date.getTime() - b.date.getTime())
  const past = dated
    .filter((entry) => entry.date.getTime() < today)
    .toSorted((a, b) => b.date.getTime() - a.date.getTime())

  return {
    upcoming: upcoming.map((entry) => entry.appointment),
    past: [...past.map((entry) => entry.appointment), ...undated],
  }
}

/** The soonest appointment from today on that is still going ahead. */
export function nextAppointment(
  appointments: AppointmentRecord[],
  now: Date
): AppointmentRecord | null {
  return (
    splitAppointments(appointments, now).upcoming.find(isAppointmentActive) ??
    null
  )
}

function visitDate(visit: VisitRecord): Date | null {
  return parseAppointmentDate(visit.visit_date)
}

/** Most recent visit date, or null when no visit carries one. */
export function lastVisitDate(visits: VisitRecord[]): Date | null {
  let latest: Date | null = null
  for (const visit of visits) {
    const date = visitDate(visit)
    if (date && (!latest || date > latest)) latest = date
  }
  return latest
}

/** Shown for a provider id the provider list does not name. */
export const UNNAMED_PROVIDER = 'Unnamed provider'

export interface CareTeamMember {
  /** Null for an assigned provider who has no visits on file. */
  providerId: string | null
  name: string
  visitCount: number
  lastVisit: Date | null
  /** The client's assigned provider (`provider_info.name`). */
  assigned: boolean
  /**
   * Not seen the client in CLIENT_CARE_TEAM_INACTIVE_MONTHS. Providers have no
   * active flag, so visit recency stands in; the assigned provider never is.
   */
  inactive: boolean
}

/** Provider names arrive as "Last, First" in one system and "First Last" in another. */
function nameKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .toSorted()
    .join(' ')
}

/**
 * Everyone who has seen the client, from visit history, with the assigned
 * provider first.
 *
 * Visits carry only `provider_id`, so names come from the org's provider list.
 * A provider missing from that list (it is read one page deep) still counts,
 * as an unnamed provider, so the client's visits are never silently dropped. The assigned provider is matched to a visit provider by
 * name, ignoring order and punctuation; when nobody matches they still appear,
 * with no visits.
 */
export function buildCareTeam(
  visits: VisitRecord[],
  providerNames: ReadonlyMap<string, string>,
  assignedName: string | null,
  now: Date
): CareTeamMember[] {
  const activeSince = subMonths(now, CLIENT_CARE_TEAM_INACTIVE_MONTHS)
  const byProvider = new Map<string, CareTeamMember>()

  for (const visit of visits) {
    if (!visit.provider_id) continue
    const name =
      providerNames.get(visit.provider_id)?.trim() || UNNAMED_PROVIDER

    let member = byProvider.get(visit.provider_id)
    if (!member) {
      member = {
        providerId: visit.provider_id,
        name,
        visitCount: 0,
        lastVisit: null,
        assigned: false,
        inactive: false,
      }
      byProvider.set(visit.provider_id, member)
    }
    member.visitCount += 1
    const date = visitDate(visit)
    if (date && (!member.lastVisit || date > member.lastVisit)) {
      member.lastVisit = date
    }
  }

  for (const member of byProvider.values()) {
    member.inactive = !member.lastVisit || member.lastVisit < activeSince
  }

  const members = [...byProvider.values()].toSorted(
    (a, b) =>
      (b.lastVisit?.getTime() ?? 0) - (a.lastVisit?.getTime() ?? 0) ||
      b.visitCount - a.visitCount
  )

  const assigned = assignedName?.trim()
  if (!assigned) return members

  const key = nameKey(assigned)
  const match = members.find((member) => nameKey(member.name) === key)
  if (match) {
    return [
      { ...match, assigned: true, inactive: false },
      ...members.filter((member) => member !== match),
    ]
  }
  return [
    {
      providerId: null,
      name: assigned,
      visitCount: 0,
      lastVisit: null,
      assigned: true,
      inactive: false,
    },
    ...members,
  ]
}

/**
 * eCW writes appointment types as "CODE : Description" ("MAT FUP : MAT Follow
 * Up"); other sources send one or the other.
 */
export function splitAppointmentType(type: string | null): {
  code: string | null
  description: string | null
} {
  const value = type?.trim()
  if (!value) return { code: null, description: null }
  const [code, ...rest] = value.split(' : ')
  const description = rest.join(' : ').trim()
  return description
    ? { code: code?.trim() || null, description }
    : { code: value, description: null }
}

/** "Rivera, Ana Lucia" → "Rivera, A."; names without a comma pass through. */
export function shortProviderName(name: string | null): string | null {
  const value = name?.trim()
  if (!value) return null
  const [last, first] = value.split(',').map((part) => part.trim())
  return first ? `${last}, ${first[0]?.toUpperCase()}.` : value
}

export interface VisitTypeCount {
  type: string
  count: number
}

/** Visits counted by type, most frequent first; untyped visits are left out. */
export function visitMix(visits: VisitRecord[]): VisitTypeCount[] {
  const counts = new Map<string, number>()
  for (const visit of visits) {
    const type = visit.visit_type?.trim()
    if (type) counts.set(type, (counts.get(type) ?? 0) + 1)
  }
  return [...counts]
    .map(([type, count]) => ({ type, count }))
    .toSorted((a, b) => b.count - a.count || a.type.localeCompare(b.type))
}
